/**
 * AdSlotManager — /admin/ad-slots.
 *
 * Grouped by SIZE first (Hero / Wide Banner / Sidebar / Inline / Overlay /
 * Global), then filtered by page/section. Every card shows a true-aspect
 * preview at ~200px wide with size chip, live impressions/CTR, and a
 * per-slot "Print media kit" button that opens the AdSlotReport view.
 *
 * Also hosts the boot-splash video swapper at the top of the page — one
 * upload replaces the Rick-Astley placeholder on the landing page.
 */
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import AdSlot from '../../components/AdSlot';
import BundleCreateModal from './BundleCreateModal';
import {
  Plus, Filter, Trash2, Eye, EyeOff, RefreshCw, BarChart3, TrendingUp,
  MousePointer, X, LayoutGrid, ExternalLink, Upload, Printer, Video,
  Image as ImageIcon, FileVideo, PlayCircle, Sparkles, Square,
  RectangleHorizontal, Monitor, ShieldAlert, Pencil, Copy, Archive,
  RotateCcw,
} from 'lucide-react';

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

const ROTATION_OPTIONS = [
  { k: 'rotating', l: 'Rotate across size',  hint: 'Show this creative in EVERY slot whose size matches — like a billboard rotation.' },
  { k: 'pinned',   l: 'Premium placement',   hint: 'Show this creative ONLY in the one exact slot you pick.' },
];

const CAP_OPTIONS = [
  { v: 0,  l: 'Unlimited' },
  { v: 1,  l: '1 / user / day' },
  { v: 3,  l: '3 / user / day' },
  { v: 5,  l: '5 / user / day' },
  { v: 10, l: '10 / user / day' },
];

// ---------------------------------------------------------------------------
// Size classification — every placement key maps to a size group so the
// admin can see "Hero placements", "Sidebar placements" etc. together.
// Aspect ratios drive the preview thumbnail shape.
// ---------------------------------------------------------------------------
const SIZE_GROUPS = {
  hero: {
    label:       'Hero / Full-width Video',
    tag:         'Hero',
    icon:        Monitor,
    aspect:      '1200 / 630',
    recommended: '1920×1080 video · 1200×630 image',
    tone:        'text-amber-700 bg-amber-500/10 border-amber-500/30',
  },
  banner_wide: {
    label:       'Wide Banner (970×250)',
    tag:         'Wide banner',
    icon:        RectangleHorizontal,
    aspect:      '970 / 250',
    recommended: '970×250 leaderboard',
    tone:        'text-sky-700 bg-sky-500/10 border-sky-500/30',
  },
  banner_med: {
    label:       'Medium Banner (728×250)',
    tag:         'Medium banner',
    icon:        RectangleHorizontal,
    aspect:      '728 / 250',
    recommended: '728×250 — fits inside container-constrained pages',
    tone:        'text-cyan-700 bg-cyan-500/10 border-cyan-500/30',
  },
  sidebar: {
    label:       'Sidebar / Skyscraper',
    tag:         'Sidebar',
    icon:        Square,
    aspect:      '300 / 600',
    recommended: '300×600 or 300×250',
    tone:        'text-violet-700 bg-violet-500/10 border-violet-500/30',
  },
  inline: {
    label:       'Inline / Native',
    tag:         'Inline',
    icon:        LayoutGrid,
    aspect:      '500 / 200',
    recommended: '500×200 native card',
    tone:        'text-emerald-700 bg-emerald-500/10 border-emerald-500/30',
  },
  overlay: {
    label:       'Overlay / Notification',
    tag:         'Overlay',
    icon:        Sparkles,
    aspect:      '600 / 80',
    recommended: 'Slim bar / pill',
    tone:        'text-rose-700 bg-rose-500/10 border-rose-500/30',
  },
};

// Placement → size group. Anything not listed defaults to "inline".
// Kept in sync with `LOCATION_TO_SIZE` in `server.py`.
const SIZE_MAP = {
  // Hero
  landing_hero:          'hero',
  landing_hero_content:  'hero',
  login_hero:            'hero',
  dashboard_welcome:     'hero',
  // Wide banner (970×250) — full-width sections in 1000px+ containers
  landing_ad_slot_1:     'banner_wide',
  landing_ad_slot_2:     'banner_wide',
  landing_features:      'banner_wide',
  landing_footer:        'banner_wide',
  store_banner:          'banner_wide',
  // Medium banner (728×250) — inside container-constrained pages
  login_footer:          'banner_med',
  dashboard_header:      'banner_med',
  dashboard_banner:      'banner_med',
  dashboard_footer:      'banner_med',
  database_header:       'banner_med',
  recipes_header:        'banner_med',
  business_header:       'banner_med',
  forum_header:          'banner_med',
  videos_header:         'banner_med',
  partners_header:       'banner',
  store_banner:          'banner',
  learning_between:      'banner',
  course_footer:         'banner',
  lesson_below:          'banner',
  // Sidebar
  login_sidebar:         'sidebar',
  dashboard_sidebar:     'sidebar',
  database_sidebar:      'sidebar',
  aichat_sidebar:        'sidebar',
  recipes_sidebar:       'sidebar',
  business_sidebar:      'sidebar',
  forum_sidebar:         'sidebar',
  community_side:        'sidebar',
  videos_sidebar:        'sidebar',
  learning_side:         'sidebar',
  live_hub_side:         'sidebar',
  store_side:            'sidebar',
  global_sidebar:        'sidebar',
  checkout_side:         'sidebar',
  profile_footer:        'sidebar',
  // Inline / Native
  forum_between:         'inline',
  store_featured:        'inline',
  live_room_sponsor:     'inline',
  // Overlay
  global_notification:   'overlay',
};

const SIZE_ORDER = ['hero', 'banner_wide', 'banner_med', 'sidebar', 'inline', 'overlay'];

const groupOf = (placement) => SIZE_MAP[placement] || 'inline';

// ---------------------------------------------------------------------------
// Landing hero-video swapper — inline explainer video that renders under
// the hero on the public landing page. Separate from the boot-splash.
// ---------------------------------------------------------------------------
const HeroVideoCard = ({ API }) => {
  const [setting, setSetting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ video_url: '', poster_url: '', title: '', subtitle: '', enabled: true, autoplay: false });
  const fileRef = useRef(null);
  const posterRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/settings/hero-video`);
      setSetting(r.data);
      setForm({
        video_url:  r.data.video_url  || '',
        poster_url: r.data.poster_url || '',
        title:      r.data.title      || '',
        subtitle:   r.data.subtitle   || '',
        enabled:    r.data.enabled ?? true,
        autoplay:   !!r.data.autoplay,
      });
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/hero-video`, form);
      toast.success('Hero video saved and live on the landing page');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadFile = async (file, kind /* 'video' | 'poster' */) => {
    if (!file) return;
    if (kind === 'video') {
      const ok = file.type.startsWith('video/') || /\.(mp4|mov|m4v|webm)$/i.test(file.name || '');
      if (!ok) { toast.error('Please upload a .mp4, .mov, .m4v or .webm video'); return; }
      if (file.size > 200 * 1024 * 1024) { toast.error('Video must be under 200MB'); return; }
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/admin/media-assets/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 5 * 60 * 1000,
      });
      const url = r.data?.url;
      if (!url) throw new Error('No URL returned');
      if (kind === 'video') setForm((f) => ({ ...f, video_url: url }));
      else setForm((f) => ({ ...f, poster_url: url }));
      toast.success('Upload complete');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast.error(`Upload failed: ${detail || e?.message || 'unknown'}${status ? ` (HTTP ${status})` : ''}`);
    } finally {
      setUploading(false);
      if (fileRef.current)   fileRef.current.value = '';
      if (posterRef.current) posterRef.current.value = '';
    }
  };

  return (
    <section className="pro-card p-5 mb-6 border-2 border-sky-500/40 bg-sky-50/40 dark:bg-sky-900/10" data-testid="hero-video-card">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-sky-500 text-white flex items-center justify-center flex-shrink-0">
          <PlayCircle className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eyebrow text-sky-700">Landing hero video</div>
          <h2 className="font-heading text-xl font-extrabold text-foreground mt-0.5">
            The explainer video that plays under the landing-page hero
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Distinct from the 7-second boot splash. Visitors click a poster / play button to watch. Uses your object-storage upload — no third-party player needed.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-start">
        <div className="aspect-video rounded-xl overflow-hidden border border-border bg-black relative">
          {form.video_url ? (
            <video src={form.video_url} className="w-full h-full object-cover" controls muted playsInline key={form.video_url} data-testid="hero-video-preview" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/50 text-xs">
              <PlayCircle className="w-8 h-8 opacity-40" />
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Video URL</label>
            <div className="mt-1 flex gap-2">
              <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://…mp4" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="hero-video-url" />
              <input ref={fileRef} type="file" accept="video/mp4,video/*" onChange={(e) => uploadFile(e.target.files?.[0], 'video')} className="hidden" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full font-bold whitespace-nowrap" data-testid="hero-video-upload">
                <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Poster image (optional)</label>
            <div className="mt-1 flex gap-2">
              <input value={form.poster_url} onChange={(e) => setForm({ ...form, poster_url: e.target.value })} placeholder="https://…jpg" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="hero-poster-url" />
              <input ref={posterRef} type="file" accept="image/*" onChange={(e) => uploadFile(e.target.files?.[0], 'poster')} className="hidden" />
              <Button size="sm" variant="outline" onClick={() => posterRef.current?.click()} disabled={uploading} className="rounded-full font-bold whitespace-nowrap">
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={form.title}    onChange={(e) => setForm({ ...form, title: e.target.value })}    placeholder="Video headline"      className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="hero-video-title" />
            <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="One-line subtitle" className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="hero-video-subtitle" />
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs font-bold text-foreground cursor-pointer">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="hero-video-enabled" />
              Show on landing page
            </label>
            <label className="inline-flex items-center gap-2 text-xs font-bold text-foreground cursor-pointer">
              <input type="checkbox" checked={form.autoplay} onChange={(e) => setForm({ ...form, autoplay: e.target.checked })} data-testid="hero-video-autoplay" />
              Auto-play (muted)
            </label>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={save} disabled={saving} className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="hero-video-save">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.open('/', '_blank', 'noopener')} className="rounded-full font-bold" data-testid="hero-video-open">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open landing page
            </Button>
            {setting?.updated_at && (
              <span className="text-[10px] text-muted-foreground self-center">Last updated {new Date(setting.updated_at).toLocaleString()}</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Boot-splash video swapper — top card on the manager.
// ---------------------------------------------------------------------------
const WelcomeSplashCard = ({ API }) => {
  const [setting, setSetting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [duration, setDuration] = useState(7000);
  const [enabled, setEnabled] = useState(true);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/settings/welcome-video`);
      setSetting(r.data);
      setUrlInput(r.data.video_url || '');
      setDuration(r.data.duration_ms || 7000);
      setEnabled(r.data.enabled ?? true);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/welcome-video`, {
        video_url: (urlInput || '').trim(),
        duration_ms: parseInt(duration, 10) || 7000,
        enabled,
      });
      // Reset per-day "already-seen" markers so the new splash shows up on
      // the very next landing-page mount instead of waiting until tomorrow.
      try {
        Object.keys(window.localStorage)
          .filter((k) => k.startsWith('fda_welcome_video'))
          .forEach((k) => window.localStorage.removeItem(k));
      } catch (_) { /* ignore */ }
      toast.success('Boot-splash video updated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadNew = async (file) => {
    if (!file) return;
    // Accept every video type the browser might report; server does the
    // authoritative MIME + size check.
    const ok = file.type.startsWith('video/') || /\.(mp4|mov|m4v|webm)$/i.test(file.name || '');
    if (!ok) {
      toast.error('Please upload a .mp4, .mov, .m4v or .webm video');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error('Video must be under 200MB');
      return;
    }
    setUploading(true);
    let uploadedUrl = null;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/media-assets/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 5 * 60 * 1000,
      });
      uploadedUrl = r.data?.url;
      if (!uploadedUrl) throw new Error('Upload returned no URL');
      setUrlInput(uploadedUrl);
      toast.success('Upload complete — saving as boot-splash…');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg =
        detail ? `${detail}${status ? ` (HTTP ${status})` : ''}`
        : e?.message === 'Network Error' ? 'Network error — check your connection and retry'
        : e?.code === 'ECONNABORTED' ? 'Upload timed out — try a smaller file or a faster connection'
        : `Upload failed${status ? ` — HTTP ${status}` : ''}`;
      toast.error(`Step 1 (upload) failed: ${msg}`);
      console.error('Welcome-splash upload failed', { status, detail, error: e });
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    // Step 2 — persist the new URL as the boot-splash setting. Isolated
    // try/catch so admins see EXACTLY which step failed if this breaks.
    try {
      await axios.put(`${API}/admin/settings/welcome-video`, {
        video_url: uploadedUrl,
        duration_ms: parseInt(duration, 10) || 7000,
        enabled: true,
      });
      // Clear the "already-seen-today" stamps so this admin (and anyone
      // else) sees the new splash on the very next landing-page load
      // instead of waiting until tomorrow. Only clears our own keys.
      try {
        Object.keys(window.localStorage)
          .filter((k) => k.startsWith('fda_welcome_video'))
          .forEach((k) => window.localStorage.removeItem(k));
      } catch (_) { /* ignore */ }
      toast.success('New splash saved and live on the landing page');
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast.error(`Step 2 (save) failed: ${detail || 'setting write rejected'}${status ? ` (HTTP ${status})` : ''} — paste the URL manually and click Save changes`);
      console.error('Welcome-splash save failed', { status, detail, error: e, uploadedUrl });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Force-preview the current video URL on the landing page, bypassing the
  // "shown once per day" localStorage gate. Opens in a new tab so the admin
  // can compare against the old one without losing this settings screen.
  const testPlayback = () => {
    try {
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith('fda_welcome_video'))
        .forEach((k) => window.localStorage.removeItem(k));
    } catch (_) { /* ignore */ }
    window.open('/', '_blank', 'noopener');
  };

  return (
    <section className="pro-card p-5 mb-6 border-2 border-amber-500/40 bg-amber-50/40 dark:bg-amber-900/10" data-testid="welcome-splash-card">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
          <FileVideo className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eyebrow text-amber-700">Boot-splash video</div>
          <h2 className="font-heading text-xl font-extrabold text-foreground mt-0.5">
            The 7-second video that plays when someone lands on the site
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Upload an MP4 (or .mov / .m4v / .webm) or paste a video URL. This
            replaces the current Rick-Astley placeholder immediately for
            everyone. Max 200 MB, 1920×1080 works best.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-start">
        <div className="aspect-video rounded-xl overflow-hidden border border-border bg-black relative">
          {(urlInput || setting?.video_url) ? (
            <video src={urlInput || setting.video_url} className="w-full h-full object-cover" controls muted playsInline key={urlInput || setting.video_url} data-testid="welcome-splash-preview" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/50 text-xs">
              <PlayCircle className="w-8 h-8 opacity-40" />
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Video URL</label>
            <div className="mt-1 flex flex-wrap gap-2 min-w-0">
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="/welcome.mp4 or https://…"
                className="flex-1 min-w-0 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                data-testid="welcome-splash-url"
              />
              <input
                ref={fileRef}
                type="file"
                accept="video/mp4,video/*"
                onChange={(e) => uploadNew(e.target.files?.[0])}
                className="hidden"
                data-testid="welcome-splash-file"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="rounded-full font-bold whitespace-nowrap"
                data-testid="welcome-splash-upload-btn"
              >
                <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload MP4'}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Duration (ms)</label>
              <input
                type="number"
                min={1000} max={60000} step={500}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                data-testid="welcome-splash-duration"
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-xs font-bold text-foreground cursor-pointer" data-testid="welcome-splash-enabled-wrapper">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="welcome-splash-enabled" />
                Show splash to visitors
              </label>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={save} disabled={saving} className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="welcome-splash-save">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={testPlayback}
              className="rounded-full font-bold"
              data-testid="welcome-splash-test"
              title="Opens the landing page in a new tab and bypasses the 'shown once per day' cache so you can confirm the new video actually plays"
            >
              <PlayCircle className="w-3.5 h-3.5 mr-1" /> Test playback now
            </Button>
            {setting?.updated_at && (
              <span className="text-[10px] text-muted-foreground self-center">Last updated {new Date(setting.updated_at).toLocaleString()}</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Field helper reused by the create modal.
// ---------------------------------------------------------------------------
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

// ---------------------------------------------------------------------------
// Quick-create modal — supports image URL, video upload, and video embed URL.
// ---------------------------------------------------------------------------
const QuickCreateModal = ({ open, onClose, defaultPlacement, placements, onCreated, API, editing }) => {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    placement: defaultPlacement || '',
    media_type: 'image',   // image | video | video_embed
    image_url: '',
    video_url: '',
    embed_url: '',
    title: '',
    description: '',
    click_url: '',
    cta_text: 'Learn more',
    priority: 'medium',
    display_order: 0,
    active: true,
    // Rotating-billboard controls. Defaults to rotating because that's
    // the new best-practice ("upload once, run everywhere your size fits").
    // Admins flip to "pinned" for premium placements.
    rotation_mode: 'rotating',
    size: '',   // hero | banner | sidebar | inline | overlay
    max_impressions_per_user_per_day: 0, // 0 = unlimited
    bypass_cap_for_admin: true, // admins exempt from cap by default (QA whitelist)
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sizeInfo, setSizeInfo] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!defaultPlacement) return;
    setForm((f) => ({ ...f, placement: defaultPlacement, size: f.size || SIZE_MAP[defaultPlacement] || 'inline' }));
  }, [defaultPlacement]);
  // Prefill from the campaign being edited whenever the modal opens in
  // edit mode. Runs once per `editing` object identity so re-renders
  // don't stomp the user's in-progress edits.
  useEffect(() => {
    if (!open || !editing) return;
    const asset = editing.media_asset || {};
    const mediaType = asset.media_type === 'video' ? 'video'
      : asset.media_type === 'video_embed' ? 'video_embed'
      : 'image';
    setForm({
      placement: editing.location || '',
      media_type: mediaType,
      image_url: mediaType === 'image' ? (asset.file_url || '') : '',
      video_url: mediaType === 'video' ? (asset.file_url || '') : '',
      embed_url: mediaType === 'video_embed' ? (asset.embed_url || '') : '',
      title: editing.custom_title || editing.name || '',
      description: editing.custom_description || '',
      click_url: editing.click_url || '',
      cta_text: editing.custom_cta_text || 'Learn more',
      priority: editing.priority || 'medium',
      display_order: editing.display_order || 0,
      active: editing.active !== false,
      rotation_mode: editing.rotation_mode || 'pinned',
      size: editing.size || '',
      max_impressions_per_user_per_day: editing.max_impressions_per_user_per_day || 0,
      bypass_cap_for_admin: editing.bypass_cap_for_admin !== false,
    });
  }, [open, editing]);
  // When the modal opens, pull the placement→size map + slot counts so
  // admins see "Banner · 15 eligible slots" next to each size option.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    axios.get(`${API}/admin/ad-placement-sizes`).then((r) => { if (alive) setSizeInfo(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [open, API]);
  if (!open) return null;

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/admin/media-assets/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (form.media_type === 'video') setForm((f) => ({ ...f, video_url: r.data.url }));
      else setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success('Uploaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const submit = async () => {
    if (form.rotation_mode === 'pinned' && !form.placement) { toast.info('Pick a premium placement'); return; }
    if (form.rotation_mode === 'rotating' && !form.size) { toast.info('Pick a size to rotate across'); return; }
    if (form.media_type === 'image' && !form.image_url.trim())     { toast.info('Image URL required');   return; }
    if (form.media_type === 'video' && !form.video_url.trim())     { toast.info('Video URL required');   return; }
    if (form.media_type === 'video_embed' && !form.embed_url.trim()) { toast.info('Embed URL required'); return; }
    // For rotating campaigns without an explicit placement, seed with
    // the first slot of that size so the backend's `location` field stays
    // populated. The rotation logic uses `size` + `rotation_mode` — the
    // seeded location is only used as a display fallback for legacy paths.
    let placementForSubmit = form.placement;
    if (form.rotation_mode === 'rotating' && !placementForSubmit && sizeInfo) {
      const bucket = (sizeInfo.sizes || []).find((s) => s.size === form.size);
      placementForSubmit = bucket?.slots?.[0] || '';
    }
    if (!placementForSubmit) { toast.info('No slot available for that size — pick a specific placement'); return; }
    try {
      setSaving(true);
      const payload = { ...form, placement: placementForSubmit };
      const { data } = isEdit
        ? await axios.put(`${API}/admin/ad-slots/quick/${editing.id}`, payload)
        : await axios.post(`${API}/admin/ad-slots/quick`, payload);
      toast.success(isEdit
        ? 'Ad updated'
        : (form.rotation_mode === 'rotating' ? 'Rotating creative live across matching slots' : 'Premium placement created'));
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEdit ? 'Update failed' : 'Create failed'));
    } finally { setSaving(false); }
  };

  const eligibleSlotsForSize = form.size && sizeInfo
    ? (sizeInfo.sizes || []).find((s) => s.size === form.size)?.slot_count || 0
    : 0;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4" data-testid="ad-quick-create-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-2xl p-5 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2 mb-3">
          <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Plus className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">{isEdit ? 'Edit ad' : 'Slot ad'}</div>
            <h3 className="font-heading text-lg font-extrabold text-foreground">{isEdit ? 'Edit ad campaign' : 'Add ad to slot'}</h3>
          </div>
          <button type="button" onClick={onClose} className="ml-auto h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Rotation mode" required>
            <div className="inline-flex rounded-full border border-border overflow-hidden text-[11px] font-bold w-full">
              {ROTATION_OPTIONS.map((r) => (
                <button
                  key={r.k}
                  type="button"
                  onClick={() => setForm({ ...form, rotation_mode: r.k })}
                  className={`flex-1 h-9 px-2 uppercase tracking-widest transition-colors ${form.rotation_mode === r.k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`ad-quick-rotation-${r.k}`}
                  title={r.hint}
                >
                  {r.l}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground italic">
              {ROTATION_OPTIONS.find((r) => r.k === form.rotation_mode)?.hint}
            </p>
          </Field>

          {form.rotation_mode === 'rotating' && (
            <Field label="Creative size" required>
              <select
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                data-testid="ad-quick-size"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Select a size…</option>
                {SIZE_ORDER.map((k) => {
                  const g = SIZE_GROUPS[k];
                  const bucket = (sizeInfo?.sizes || []).find((s) => s.size === k);
                  const count = bucket?.slot_count || 0;
                  return (
                    <option key={k} value={k}>
                      {g.label} — {g.recommended} · {count} eligible slot{count === 1 ? '' : 's'}
                    </option>
                  );
                })}
              </select>
              {form.size && (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Will rotate across <b>{eligibleSlotsForSize}</b> slot{eligibleSlotsForSize === 1 ? '' : 's'} site-wide.
                </p>
              )}
            </Field>
          )}

          {form.rotation_mode === 'pinned' && (
            <Field label="Premium placement" required>
              <select
                value={form.placement}
                onChange={(e) => setForm({ ...form, placement: e.target.value, size: SIZE_MAP[e.target.value] || 'inline' })}
                data-testid="ad-quick-placement"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Select premium placement…</option>
                {placements.map((p) => (
                  <option key={p.value} value={p.value}>
                    [{(p.size || SIZE_MAP[p.value] || 'inline').toUpperCase()}] {p.label} — {p.description || p.value}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Media type" required>
            <div className="inline-flex rounded-full border border-border overflow-hidden text-[11px] font-bold w-full">
              {[
                { k: 'image',       l: 'Image',       icon: ImageIcon },
                { k: 'video',       l: 'Video file',  icon: FileVideo },
                { k: 'video_embed', l: 'Embed URL',   icon: Video     },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setForm({ ...form, media_type: m.k })}
                  className={`flex-1 h-9 px-2 uppercase tracking-widest transition-colors inline-flex items-center justify-center gap-1 ${form.media_type === m.k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`ad-quick-media-${m.k}`}
                >
                  <m.icon className="w-3 h-3" /> {m.l}
                </button>
              ))}
            </div>
          </Field>

          {form.media_type === 'image' && (
            <Field label="Image URL" required>
              <div className="flex gap-2">
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…jpg" data-testid="ad-quick-image-url" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadMedia(e.target.files?.[0])} className="hidden" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full font-bold whitespace-nowrap" data-testid="ad-quick-upload-image">
                  <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </div>
              {form.image_url && <img src={form.image_url} alt="preview" className="mt-2 rounded-lg max-h-32 object-cover border border-border" />}
            </Field>
          )}
          {form.media_type === 'video' && (
            <Field label="Video URL (MP4)" required>
              <div className="flex gap-2">
                <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="/api/uploads/…mp4" data-testid="ad-quick-video-url" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                <input ref={fileRef} type="file" accept="video/mp4,video/*" onChange={(e) => uploadMedia(e.target.files?.[0])} className="hidden" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full font-bold whitespace-nowrap" data-testid="ad-quick-upload-video">
                  <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </div>
              {form.video_url && <video src={form.video_url} className="mt-2 rounded-lg max-h-40 border border-border" controls muted playsInline />}
            </Field>
          )}
          {form.media_type === 'video_embed' && (
            <Field label="Embed URL (YouTube / Vimeo)" required>
              <input value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })} placeholder="https://youtube.com/watch?v=…" data-testid="ad-quick-embed-url" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Field label="Title">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Upgrade to Pro" data-testid="ad-quick-title" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </Field>
            <Field label="CTA text">
              <input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} placeholder="Learn more" data-testid="ad-quick-cta" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Description">
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ad-quick-desc" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Destination URL (on-site or external)">
              <input
                value={form.click_url}
                onChange={(e) => setForm({ ...form, click_url: e.target.value })}
                placeholder="/pricing or https://…"
                data-testid="ad-quick-click-url"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Priority (weight)">
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="ad-quick-priority" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Per-visitor frequency cap">
            <select
              value={form.max_impressions_per_user_per_day}
              onChange={(e) => setForm({ ...form, max_impressions_per_user_per_day: parseInt(e.target.value, 10) || 0 })}
              data-testid="ad-quick-freq-cap"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              {CAP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <p className="mt-1 text-[10px] text-muted-foreground italic">
              Prevents one creative from dominating a single visitor's session across page loads. Anonymous visitors are exempt (no user id to track).
            </p>
            <label className={`mt-2 flex items-center gap-2 text-xs font-medium ${!form.max_impressions_per_user_per_day ? 'opacity-40' : 'cursor-pointer'}`} title="When on, admin accounts ignore this cap so staff QA'ing on production isn't blocked after N page reloads.">
              <input
                type="checkbox"
                checked={form.bypass_cap_for_admin}
                onChange={(e) => setForm({ ...form, bypass_cap_for_admin: e.target.checked })}
                disabled={!form.max_impressions_per_user_per_day}
                data-testid="ad-quick-bypass-cap-admin"
              />
              <span>Bypass cap for admin viewers (QA whitelist)</span>
            </label>
          </Field>
        </div>

        {/* Live Preview — how this creative will actually render on
            mobile (390px) vs desktop (1440px) so the admin catches
            layout bugs before publish. Uses the same size ceilings as
            AdDisplay so the preview matches production 1:1. */}
        <AdPreviewGrid form={form} />

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={saving} className="rounded-full font-bold" data-testid="ad-quick-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving} className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="ad-quick-submit">
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create ad')}
          </Button>
        </div>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// AdPreviewGrid — side-by-side 390px mobile + full-width desktop simulation
// of the ad-in-slot render. Mirrors AdDisplay.js's SIZE_CEILINGS + render so
// admins catch mobile-collapse / oversize bugs before Publish.
// ---------------------------------------------------------------------------
const PREVIEW_CEILINGS = {
  hero:        { maxWidth: null, maxHeight: 420 }, // 100% of parent
  banner_wide: { maxWidth: null, maxHeight: 260 },
  banner_med:  { maxWidth: 728,  maxHeight: 250 },
  banner:      { maxWidth: null, maxHeight: 260 },
  sidebar:     { maxWidth: 320,  maxHeight: 620 },
  inline:      { maxWidth: 640,  maxHeight: 220 },
  overlay:     { maxWidth: 640,  maxHeight: 120 },
};

const AdPreviewFrame = ({ label, frameWidth, form }) => {
  const size = form.size || 'inline';
  const ceil = PREVIEW_CEILINGS[size] || PREVIEW_CEILINGS.inline;
  const hasMedia =
    (form.media_type === 'image' && !!form.image_url) ||
    (form.media_type === 'video' && !!form.video_url) ||
    (form.media_type === 'video_embed' && !!form.embed_url);
  const cardMax = ceil.maxWidth ? Math.min(ceil.maxWidth, frameWidth - 32) : frameWidth - 32;

  const media = () => {
    if (form.media_type === 'image' && form.image_url) {
      return (
        <img src={form.image_url} alt={form.title || 'Ad preview'}
             className="w-full h-auto rounded"
             style={{ maxWidth: '100%', maxHeight: ceil.maxHeight, objectFit: 'contain' }} />
      );
    }
    if (form.media_type === 'video' && form.video_url) {
      return (
        <video src={form.video_url} className="w-full h-auto rounded"
               style={{ maxWidth: '100%', maxHeight: ceil.maxHeight, objectFit: 'contain' }}
               controls muted playsInline />
      );
    }
    if (form.media_type === 'video_embed' && form.embed_url) {
      return (
        <div className="w-full rounded bg-slate-900 text-white/70 text-xs flex items-center justify-center"
             style={{ height: 180 }}>
          Embedded video · {form.embed_url.slice(0, 40)}…
        </div>
      );
    }
    return (
      <div className="w-full rounded border border-dashed border-slate-300 bg-slate-50 text-slate-400 text-[11px] flex items-center justify-center"
           style={{ height: 120 }}>
        No media yet
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-muted/30 p-3 overflow-hidden" data-testid={`ad-preview-frame-${label}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
        {label} · {frameWidth}px
      </div>
      <div className="mx-auto rounded-lg overflow-hidden bg-white border border-slate-200" style={{ width: `min(${frameWidth}px, 100%)` }}>
        <div className="p-4 flex justify-center">
          <div className="relative block w-full bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm"
               style={{ maxWidth: cardMax || '100%' }}
               data-testid={`ad-preview-card-${label}`}>
            {media()}
            {(form.title || form.description) && hasMedia && (
              <div className="p-3">
                {form.title && <h3 className="font-semibold text-sm text-slate-900 mb-1">{form.title}</h3>}
                {form.description && <p className="text-xs text-slate-600 mb-2 line-clamp-3">{form.description}</p>}
                {form.click_url && form.cta_text && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-primary">{form.cta_text}</span>
                )}
              </div>
            )}
            <div className="absolute bottom-2 left-2 bg-slate-800/70 text-white text-[10px] px-2 py-0.5 rounded">Ad</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdPreviewGrid = ({ form }) => (
  <div className="mt-4 pt-4 border-t border-border overflow-hidden" data-testid="ad-preview-grid">
    <div className="flex items-center gap-2 mb-2">
      <Eye className="w-4 h-4 text-primary" />
      <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
        Live preview — how it will look before publish
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
      <div className="min-w-0">
        <AdPreviewFrame label="Mobile"  frameWidth={390}  form={form} />
      </div>
      <div className="min-w-0 overflow-x-auto">
        <AdPreviewFrame label="Desktop" frameWidth={1440} form={form} />
      </div>
    </div>
    <p className="mt-2 text-[10px] text-muted-foreground italic">
      Uses the exact same size ceilings as the live AdDisplay renderer. If the ad looks squeezed here, it will look squeezed live — swap the creative or pick a different size before saving.
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Slot card — 200px-wide true-aspect preview + size chip + print report.
// ---------------------------------------------------------------------------
const SlotCard = ({ slot, group, onAdd, onExpand, expanded, onToggleActive, onDelete, onPreview, onEdit }) => {
  const Icon = group.icon;
  return (
    <article className={`pro-card p-4 ${expanded ? 'ring-1 ring-primary/40' : ''}`} data-testid={`ad-slot-${slot.placement}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 rounded-xl border border-dashed border-border bg-muted/30 overflow-hidden" style={{ width: 200, aspectRatio: group.aspect }}>
          {slot.active_count > 0 ? (
            <div className="w-full h-full p-1"><AdSlot location={slot.placement} variant="inline" /></div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-1">
              <EyeOff className="w-4 h-4 opacity-50" />
              <span className="text-[10px] uppercase tracking-widest font-bold">Empty</span>
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${group.tone}`}>
              <Icon className="w-3 h-3" /> {group.tag}
            </span>
            <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${slot.active_count > 0 ? 'bg-emerald-500/15 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
              {slot.active_count > 0 ? `${slot.active_count} live` : 'empty'}
            </span>
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest font-bold text-primary truncate">{slot.placement}</div>
          <div className="font-heading text-sm font-extrabold text-foreground truncate">{slot.label}</div>
          {slot.description && <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{slot.description}</div>}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
            <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {slot.impressions.toLocaleString()} imp</span>
            <span className="inline-flex items-center gap-1"><MousePointer className="w-3 h-3" /> {slot.clicks.toLocaleString()} clicks</span>
            <span className="inline-flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {slot.ctr_pct}% CTR</span>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={onAdd} className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`ad-slot-add-${slot.placement}`}>
              <Plus className="w-3 h-3 mr-1" /> Add ad
            </Button>
            <Link
              to={`/admin/ad-slots/report/${slot.placement}`}
              className="inline-flex items-center gap-1 rounded-full px-3 h-8 text-xs font-bold border border-border hover:bg-muted transition-colors"
              data-testid={`ad-slot-report-${slot.placement}`}
            >
              <Printer className="w-3 h-3" /> Media kit
            </Link>
            <button
              type="button"
              onClick={() => onPreview && onPreview(slot.placement)}
              className="inline-flex items-center gap-1 rounded-full px-3 h-8 text-xs font-bold border border-border hover:bg-muted transition-colors"
              data-testid={`ad-slot-preview-${slot.placement}`}
              title="Simulate the next 10 rolls of the rotation for this slot"
            >
              <RefreshCw className="w-3 h-3" /> Preview rotation
            </button>
            {slot.campaign_count > 0 && (
              <Button size="sm" variant="outline" onClick={onExpand} className="rounded-full font-bold" data-testid={`ad-slot-expand-${slot.placement}`}>
                {expanded ? 'Hide' : `Manage (${slot.campaign_count})`}
              </Button>
            )}
          </div>
        </div>
      </div>

      {expanded && slot.campaigns.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid={`ad-slot-campaigns-${slot.placement}`}>
          {slot.campaigns.map((c) => (
            <li key={c.id} className="flex items-center gap-2 rounded-lg border border-border bg-background/60 p-2">
              {c.media_asset?.file_url && (
                <img src={c.media_asset.file_url} alt="" className="h-10 w-10 rounded object-cover flex-shrink-0" />
              )}
              {!c.media_asset?.file_url && c.media_asset?.media_type === 'video_embed' && (
                <div className="h-10 w-10 rounded bg-primary/15 text-primary flex items-center justify-center flex-shrink-0"><Video className="w-4 h-4" /></div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-foreground truncate flex items-center gap-1.5">
                  {c.name || c.custom_title || 'Untitled'}
                  {c.rotation_mode === 'rotating' && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black bg-amber-500/20 text-amber-800 dark:text-amber-300 rounded-full px-1.5 py-0.5" title={`Rotates across every ${c.size || 'inline'} slot site-wide`}>
                      <RefreshCw className="w-2.5 h-2.5" /> Rotating · {c.size || 'inline'}
                    </span>
                  )}
                  {c.rotation_mode !== 'rotating' && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black bg-primary/15 text-primary rounded-full px-1.5 py-0.5" title="Pinned to this exact placement">
                      <Sparkles className="w-2.5 h-2.5" /> Premium
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {c.priority} · {c.impressions} imp · {c.clicks} clicks · {c.ctr_pct}% CTR
                </div>
              </div>
              <button
                type="button"
                onClick={() => onEdit && onEdit(c)}
                className="h-7 w-7 rounded-full flex items-center justify-center text-sky-700 hover:bg-sky-500/10"
                title="Edit ad"
                data-testid={`ad-campaign-edit-${c.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onToggleActive(c, !c.active)}
                className={`h-7 w-7 rounded-full flex items-center justify-center ${c.active ? 'text-emerald-600 hover:bg-emerald-500/10' : 'text-muted-foreground hover:bg-muted'}`}
                title={c.active ? 'Pause' : 'Activate'}
                data-testid={`ad-campaign-toggle-${c.id}`}
              >
                {c.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
              {c.click_url && (
                <a href={c.click_url} target="_blank" rel="noreferrer" className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted" title="Open click URL">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <button type="button" onClick={() => onDelete(c)} className="h-7 w-7 rounded-full flex items-center justify-center text-rose-600 hover:bg-rose-500/10" title="Delete" data-testid={`ad-campaign-delete-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};

// ---------------------------------------------------------------------------
// Main manager component.
// ---------------------------------------------------------------------------
const AdSlotManager = () => {
  const { API } = useContext(AuthContext);
  const [slots, setSlots] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active|empty|all
  const [sizeFilter, setSizeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createPlacement, setCreatePlacement] = useState('');
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [bundleOpen, setBundleOpen] = useState(false);
  const [previewLocation, setPreviewLocation] = useState(null);
  const [viewMode, setViewMode] = useState('rotating'); // rotating | by-slot
  const [audit, setAudit] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ov, locs, aud] = await Promise.all([
        axios.get(`${API}/admin/ad-slots/overview${showArchived ? '?include_archived=true' : ''}`),
        axios.get(`${API}/admin/ad-locations`),
        axios.get(`${API}/admin/ad-slots/audit`),
      ]);
      setSlots(ov.data.slots || []);
      setPlacements(locs.data || []);
      setAudit(aud.data || null);
    } catch (err) {
      toast.error('Could not load ad slots');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showArchived]);

  const filtered = useMemo(() => {
    let list = slots;
    if (filter === 'active') list = list.filter((s) => s.active_count > 0);
    if (filter === 'empty')  list = list.filter((s) => s.campaign_count === 0);
    if (sizeFilter !== 'all') list = list.filter((s) => groupOf(s.placement) === sizeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((s) => s.placement.includes(q) || (s.label || '').toLowerCase().includes(q));
    }
    return list;
  }, [slots, filter, sizeFilter, search]);

  // Group filtered slots by size.
  const grouped = useMemo(() => {
    const g = {};
    for (const s of filtered) {
      const key = groupOf(s.placement);
      (g[key] = g[key] || []).push(s);
    }
    return g;
  }, [filtered]);

  const toggleActive = async (campaign, next) => {
    try {
      await axios.put(`${API}/admin/ad-campaigns/${campaign.id}`, { active: next });
      toast.success(next ? 'Campaign activated' : 'Campaign paused');
      load();
    } catch (_) { toast.error('Update failed'); }
  };
  const deleteCampaign = async (campaign) => {
    // In the Archived view, delete is permanent (hard=true). Otherwise
    // we soft-archive so admins can revive last quarter's winner.
    if (campaign.archived) {
      if (!window.confirm(`Permanently delete "${campaign.name}"? This can't be undone.`)) return;
      try {
        await axios.delete(`${API}/admin/ad-campaigns/${campaign.id}?hard=true`);
        toast.success('Permanently deleted');
        load();
      } catch (_) { toast.error('Delete failed'); }
      return;
    }
    if (!window.confirm(`Archive "${campaign.name}"? You can restore it later from the Archived tab.`)) return;
    try {
      await axios.delete(`${API}/admin/ad-campaigns/${campaign.id}`);
      toast.success('Archived');
      load();
    } catch (_) { toast.error('Archive failed'); }
  };

  const cloneCampaign = async (campaign) => {
    try {
      const { data } = await axios.post(`${API}/admin/ad-campaigns/${campaign.id}/clone`);
      toast.success('Duplicated — edit and save to publish');
      // Reload so the new inactive copy shows up, and open the editor
      // pre-filled so the admin can tweak size/URL right away.
      await load();
      setEditingCampaign(data);
      setCreatePlacement('');
      setCreateOpen(true);
    } catch (_) { toast.error('Clone failed'); }
  };

  const restoreCampaign = async (campaign) => {
    try {
      await axios.post(`${API}/admin/ad-campaigns/${campaign.id}/restore`);
      toast.success('Restored — activate when ready');
      load();
    } catch (_) { toast.error('Restore failed'); }
  };

  const summary = useMemo(() => ({
    total_slots:      slots.length,
    active_slots:     slots.filter((s) => s.active_count > 0).length,
    empty_slots:      slots.filter((s) => s.campaign_count === 0).length,
    total_campaigns:  slots.reduce((a, s) => a + s.campaign_count, 0),
    total_impressions: slots.reduce((a, s) => a + s.impressions, 0),
    total_clicks:     slots.reduce((a, s) => a + s.clicks, 0),
  }), [slots]);
  const overallCtr = summary.total_impressions
    ? Math.round((summary.total_clicks / summary.total_impressions) * 10000) / 100
    : 0;

  const sizeCounts = useMemo(() => {
    const counts = { all: slots.length };
    for (const k of SIZE_ORDER) counts[k] = 0;
    for (const s of slots) counts[groupOf(s.placement)] += 1;
    return counts;
  }, [slots]);

  // Rotating-pool view: dedupe every campaign that appears in the
  // per-slot overview into one row per campaign, keyed by campaign.id,
  // then group by `size`. This is the primary lens now — "manage
  // creatives" rather than "manage 43 slots".
  const rotatingPools = useMemo(() => {
    const byId = new Map();
    for (const s of slots) {
      for (const c of s.campaigns || []) {
        if (!byId.has(c.id)) byId.set(c.id, c);
      }
    }
    const pools = { hero: [], banner: [], sidebar: [], inline: [], overlay: [] };
    for (const c of byId.values()) {
      const key = c.size || groupOf(c.location);
      if (!pools[key]) pools[key] = [];
      pools[key].push(c);
    }
    // Rotating first, then pinned; then by impressions desc.
    for (const k of Object.keys(pools)) {
      pools[k].sort((a, b) => {
        const rotDiff = (a.rotation_mode === 'rotating' ? 0 : 1) - (b.rotation_mode === 'rotating' ? 0 : 1);
        if (rotDiff !== 0) return rotDiff;
        return (b.impressions || 0) - (a.impressions || 0);
      });
    }
    return pools;
  }, [slots]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <LayoutGrid className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="eyebrow text-primary">Ad Slot Manager</div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight">
              Every placement, grouped by size
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Grouped by size — Hero, Banner, Sidebar, Inline, Overlay — so you
              can build a matching creative pack. Each card shows a live
              true-aspect preview + impressions/CTR. Tap <b>Media kit</b> on
              any slot to print the traffic report for potential ad buyers.
            </p>
          </div>
          <Button onClick={() => setBundleOpen(true)} data-testid="ad-manager-bundle-btn" className="ml-auto rounded-full font-bold bg-amber-500 text-navy hover:bg-amber-400">
            <Sparkles className="w-4 h-4 mr-1" /> New bundle
          </Button>
          <Button onClick={() => { setCreatePlacement(''); setCreateOpen(true); }} data-testid="ad-manager-add-btn" className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-1" /> Add ad
          </Button>
        </div>

        {/* Landing hero-video swapper (the inline explainer video) */}
        <HeroVideoCard API={API} />

        {/* Boot-splash video swapper (the 7-second bootup video) */}
        <WelcomeSplashCard API={API} />

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5" data-testid="ad-manager-summary">
          <StatTile label="Slots"        value={summary.total_slots}      icon={<LayoutGrid className="w-3 h-3" />} />
          <StatTile label="Active"       value={summary.active_slots}     icon={<Eye className="w-3 h-3" />} tone="ok" />
          <StatTile label="Empty"        value={summary.empty_slots}      icon={<EyeOff className="w-3 h-3" />} tone={summary.empty_slots > 15 ? 'warn' : undefined} />
          <StatTile label="Campaigns"    value={summary.total_campaigns}  icon={<BarChart3 className="w-3 h-3" />} />
          <StatTile label="Overall CTR"  value={`${overallCtr}%`} sub={`${summary.total_clicks} / ${summary.total_impressions}`} icon={<MousePointer className="w-3 h-3" />} />
        </div>

        {/* Size chips */}
        <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="ad-manager-size-chips">
          <button
            type="button"
            onClick={() => setSizeFilter('all')}
            className={`inline-flex items-center gap-1 rounded-full px-3 h-8 text-xs font-bold uppercase tracking-widest border transition-colors ${sizeFilter === 'all' ? 'bg-foreground text-background border-foreground' : 'text-muted-foreground border-border hover:text-foreground'}`}
            data-testid="ad-size-all"
          >
            All sizes · {sizeCounts.all}
          </button>
          {SIZE_ORDER.map((k) => {
            const g = SIZE_GROUPS[k];
            const Icon = g.icon;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setSizeFilter(k)}
                className={`inline-flex items-center gap-1 rounded-full px-3 h-8 text-xs font-bold uppercase tracking-widest border transition-colors ${sizeFilter === k ? 'bg-foreground text-background border-foreground' : `${g.tone} hover:brightness-95`}`}
                data-testid={`ad-size-${k}`}
              >
                <Icon className="w-3 h-3" /> {g.tag} · {sizeCounts[k] || 0}
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="ad-manager-filter">
          <div className="inline-flex rounded-full border border-border overflow-hidden text-[11px] font-bold">
            {[
              { k: 'active', l: 'Active only' },
              { k: 'empty',  l: 'Empty'       },
              { k: 'all',    l: 'All'         },
            ].map((f) => (
              <button
                key={f.k}
                type="button"
                onClick={() => setFilter(f.k)}
                data-testid={`ad-filter-${f.k}`}
                className={`px-3 h-8 uppercase tracking-widest transition-colors ${filter === f.k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {f.l}
              </button>
            ))}
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search placements…"
            data-testid="ad-manager-search"
            className="flex-1 min-w-[180px] rounded-full border border-border bg-background px-3 h-8 text-xs"
          />
          <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="ad-manager-refresh">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className={`inline-flex items-center gap-1 rounded-full px-3 h-8 text-xs font-bold uppercase tracking-widest border transition-colors ${showArchived ? 'bg-foreground text-background border-foreground' : 'text-muted-foreground border-border hover:text-foreground'}`}
            data-testid="ad-manager-archived-toggle"
            title="Show archived ads so you can restore or permanently delete them"
          >
            <Archive className="w-3 h-3" /> {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
        </div>

        {/* Grouped slot grid */}
        {loading && <div className="text-center py-16 text-sm text-muted-foreground">Loading slots…</div>}

        {/* Primary lens: view-mode toggle. Rotating = creatives grouped
            by size (billboard model). By-slot = the legacy 43-slot grid. */}
        {!loading && (
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <div className="inline-flex rounded-full border border-border overflow-hidden text-[11px] font-bold" data-testid="ad-manager-view-mode">
              {[
                { k: 'rotating', l: 'Rotating pools',   hint: 'Manage creatives by size — the billboard view.' },
                { k: 'by-slot',  l: 'By slot (legacy)', hint: 'Every one of the 43 placements as its own card.' },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setViewMode(m.k)}
                  title={m.hint}
                  data-testid={`ad-manager-view-${m.k}`}
                  className={`px-4 h-8 uppercase tracking-widest transition-colors ${viewMode === m.k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {m.l}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setAuditOpen((v) => !v)}
              data-testid="ad-manager-audit-toggle"
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 h-8 text-[11px] font-bold uppercase tracking-widest hover:bg-muted transition-colors"
              title="Show a per-size placement + creative audit"
            >
              <ShieldAlert className="w-3 h-3" /> {auditOpen ? 'Hide audit' : 'Show audit'}
            </button>
          </div>
        )}

        {auditOpen && audit && (
          <section className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20 p-4" data-testid="ad-manager-audit-panel">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-4 h-4 text-amber-600" />
              <h2 className="font-heading font-extrabold text-sm">Placement / creative audit</h2>
              <span className="text-[11px] text-muted-foreground">{audit.total_campaigns} active campaign{audit.total_campaigns === 1 ? '' : 's'}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
              {SIZE_ORDER.map((sk) => {
                const b = audit.by_size?.[sk] || {};
                return (
                  <div key={sk} className="rounded-xl border border-border bg-background/70 p-3" data-testid={`ad-audit-${sk}`}>
                    <div className="text-[10px] uppercase tracking-widest font-black text-muted-foreground">{sk}</div>
                    <div className="text-lg font-heading font-extrabold">{b.slot_count || 0} <span className="text-xs font-normal text-muted-foreground">slots</span></div>
                    <div className="text-[11px] text-foreground">{b.creative_count || 0} creative{(b.creative_count || 0) === 1 ? '' : 's'}</div>
                    <div className="text-[10px] text-muted-foreground">{b.rotating_count || 0} rotating · {b.pinned_count || 0} pinned</div>
                    {(b.empty_slots || []).length > 0 && (
                      <div className="mt-1.5 text-[10px] text-rose-700 dark:text-rose-400 truncate" title={(b.empty_slots || []).join(', ')}>
                        {(b.empty_slots || []).length} slot{(b.empty_slots || []).length === 1 ? '' : 's'} empty
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {(audit.orphans || []).length > 0 && (
              <div className="rounded-xl border border-rose-400/50 bg-rose-50/60 dark:bg-rose-950/20 p-3">
                <div className="text-[10px] uppercase tracking-widest font-black text-rose-700 dark:text-rose-400 mb-1">
                  {audit.orphans.length} orphan campaign{audit.orphans.length === 1 ? '' : 's'} — declared size doesn't match pinned slot
                </div>
                <ul className="text-[11px] space-y-0.5" data-testid="ad-audit-orphans">
                  {audit.orphans.map((o) => (
                    <li key={o.campaign_id}>
                      <b>{o.campaign_name}</b> — pinned to <code>{o.location}</code> (size <code>{o.location_size}</code>) but declared <code>{o.declared_size}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="mt-2 text-[10px] text-muted-foreground italic">
              An "empty pool" means visitors on those slots see NOTHING. Add a rotating creative in that size to cover them all at once.
            </p>
          </section>
        )}

        {!loading && viewMode === 'rotating' && SIZE_ORDER.map((sk) => {
          const pool = rotatingPools[sk] || [];
          const g = SIZE_GROUPS[sk];
          const Icon = g.icon;
          const eligibleSlots = sizeCounts[sk] || 0;
          const rotatingCount = pool.filter((c) => c.rotation_mode === 'rotating').length;
          return (
            <section key={sk} className="mb-6" data-testid={`ad-pool-${sk}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`h-8 w-8 rounded-lg border flex items-center justify-center ${g.tone}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-heading text-lg font-extrabold text-foreground">{g.label} pool</h2>
                  <p className="text-[11px] text-muted-foreground">
                    Recommended creative: {g.recommended} · <b>{eligibleSlots}</b> eligible slot{eligibleSlots === 1 ? '' : 's'} · <b>{rotatingCount}</b> rotating / <b>{pool.length - rotatingCount}</b> pinned
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setCreatePlacement('');
                    setCreateOpen(true);
                    // stash a "seed size" that the modal picks up via defaultPlacement/size prop
                  }}
                  className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid={`ad-pool-add-${sk}`}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add {g.tag.toLowerCase()} ad
                </Button>
              </div>
              {pool.length === 0 ? (
                <div className="border border-dashed border-border rounded-xl p-6 text-center text-[12px] text-muted-foreground" data-testid={`ad-pool-empty-${sk}`}>
                  No creatives in this pool. Add one to have it rotate across every {g.tag.toLowerCase()} slot on the site.
                </div>
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`ad-pool-list-${sk}`}>
                  {pool.map((c) => (
                    <li key={c.id} className={`rounded-xl border p-3 flex items-center gap-3 ${c.rotation_mode === 'rotating' ? 'border-amber-500/40 bg-amber-500/5' : 'border-primary/30 bg-primary/5'}`} data-testid={`ad-pool-item-${c.id}`}>
                      <div className="flex-shrink-0 rounded-lg overflow-hidden border border-border bg-muted/30" style={{ width: 80, aspectRatio: g.aspect }}>
                        {c.media_asset?.file_url && c.media_asset?.media_type === 'image' && (
                          <img src={c.media_asset.file_url} alt="" className="w-full h-full object-cover" />
                        )}
                        {c.media_asset?.file_url && c.media_asset?.media_type === 'video' && (
                          <video src={c.media_asset.file_url} className="w-full h-full object-cover" muted playsInline />
                        )}
                        {(!c.media_asset?.file_url || c.media_asset?.media_type === 'video_embed') && (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Video className="w-4 h-4" /></div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {c.rotation_mode === 'rotating' ? (
                            <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black bg-amber-500/20 text-amber-800 dark:text-amber-300 rounded-full px-1.5 py-0.5">
                              <RefreshCw className="w-2.5 h-2.5" /> Rotating
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black bg-primary/15 text-primary rounded-full px-1.5 py-0.5" title={`Pinned to ${c.location}`}>
                              <Sparkles className="w-2.5 h-2.5" /> Pinned · {c.location}
                            </span>
                          )}
                          <span className="text-[9px] uppercase tracking-widest font-black text-muted-foreground">{c.priority}</span>
                          {c.max_impressions_per_user_per_day ? (
                            <span className="text-[9px] uppercase tracking-widest font-black bg-slate-500/15 text-slate-700 dark:text-slate-300 rounded-full px-1.5 py-0.5" title="Per-visitor daily cap">
                              cap {c.max_impressions_per_user_per_day}/d
                            </span>
                          ) : null}
                        </div>
                        <div className="text-sm font-bold text-foreground truncate mt-0.5">{c.name || c.custom_title || 'Untitled'}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {c.impressions} imp · {c.clicks} clicks · {c.ctr_pct}% CTR
                          {c.click_url && <> · <a href={c.click_url} target="_blank" rel="noreferrer" className="underline hover:text-foreground">{c.click_url}</a></>}
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 gap-1">
                        {c.archived && (
                          <button
                            type="button"
                            onClick={() => restoreCampaign(c)}
                            className="h-7 w-7 rounded-full flex items-center justify-center text-emerald-700 hover:bg-emerald-500/10"
                            title="Restore this archived ad"
                            data-testid={`ad-pool-restore-${c.id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => cloneCampaign(c)}
                          className="h-7 w-7 rounded-full flex items-center justify-center text-slate-700 hover:bg-slate-500/10"
                          title="Duplicate as a new variant (starts inactive)"
                          data-testid={`ad-pool-clone-${c.id}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { setEditingCampaign(c); setCreatePlacement(''); setCreateOpen(true); }}
                          className="h-7 w-7 rounded-full flex items-center justify-center text-sky-700 hover:bg-sky-500/10"
                          title="Edit ad (URL, image, weight, size)"
                          data-testid={`ad-pool-edit-${c.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(c, !c.active)}
                          className={`h-7 w-7 rounded-full flex items-center justify-center ${c.active ? 'text-emerald-600 hover:bg-emerald-500/10' : 'text-muted-foreground hover:bg-muted'}`}
                          title={c.active ? 'Pause' : 'Activate'}
                          data-testid={`ad-pool-toggle-${c.id}`}
                        >
                          {c.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewLocation(c.location)}
                          className="h-7 w-7 rounded-full flex items-center justify-center text-amber-700 hover:bg-amber-500/10"
                          title="Preview rotation for this size"
                          data-testid={`ad-pool-preview-${c.id}`}
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteCampaign(c)}
                          className="h-7 w-7 rounded-full flex items-center justify-center text-rose-600 hover:bg-rose-500/10"
                          title={c.archived ? 'Permanently delete' : 'Archive (recoverable)'}
                          data-testid={`ad-pool-delete-${c.id}`}
                        >
                          {c.archived ? <Trash2 className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}

        {!loading && viewMode === 'by-slot' && SIZE_ORDER.map((sk) => {
          const list = grouped[sk] || [];
          if (list.length === 0) return null;
          const g = SIZE_GROUPS[sk];
          const Icon = g.icon;
          return (
            <section key={sk} className="mb-6" data-testid={`ad-size-group-${sk}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`h-8 w-8 rounded-lg border flex items-center justify-center ${g.tone}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-extrabold text-foreground">{g.label}</h2>
                  <p className="text-[11px] text-muted-foreground">Recommended creative: {g.recommended} · {list.length} slot{list.length === 1 ? '' : 's'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid={`ad-manager-grid-${sk}`}>
                {list.map((s) => (
                  <SlotCard
                    key={s.placement}
                    slot={s}
                    group={g}
                    expanded={expanded === s.placement}
                    onAdd={() => { setCreatePlacement(s.placement); setCreateOpen(true); }}
                    onExpand={() => setExpanded(expanded === s.placement ? null : s.placement)}
                    onToggleActive={toggleActive}
                    onDelete={deleteCampaign}
                    onEdit={(c) => { setEditingCampaign(c); setCreatePlacement(''); setCreateOpen(true); }}
                    onPreview={(loc) => setPreviewLocation(loc)}
                  />
                ))}
              </div>
            </section>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 border border-dashed border-border rounded-2xl">
            <Filter className="w-6 h-6 mx-auto text-muted-foreground opacity-50 mb-2" />
            <div className="text-sm text-muted-foreground">No slots match this filter.</div>
          </div>
        )}
      </div>

      <QuickCreateModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setEditingCampaign(null); }}
        defaultPlacement={createPlacement}
        placements={placements}
        API={API}
        editing={editingCampaign}
        onCreated={() => { setCreateOpen(false); setEditingCampaign(null); load(); }}
      />
      <BundleCreateModal
        open={bundleOpen}
        onClose={() => setBundleOpen(false)}
        placements={placements}
        onCreated={() => { setBundleOpen(false); load(); }}
      />
      <RotationPreviewModal
        open={!!previewLocation}
        location={previewLocation}
        onClose={() => setPreviewLocation(null)}
        API={API}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Rotation preview modal — "next 10 rolls" simulation for a given slot.
// Uses POST /api/admin/ads/rotation-preview which runs the same picker
// used at request time. Read-only (does NOT touch impression counters).
// ---------------------------------------------------------------------------
const RotationPreviewModal = ({ open, location, onClose, API }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rolls, setRolls] = useState(10);

  const run = React.useCallback(async () => {
    if (!location) return;
    setLoading(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/ads/rotation-preview`, { location, rolls });
      setData(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  }, [API, location, rolls]);

  useEffect(() => { if (open && location) run(); }, [open, location, run]);

  if (!open || !location) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4" data-testid="rotation-preview-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-2xl p-5 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2 mb-3">
          <div className="h-9 w-9 rounded-xl bg-amber-500 text-navy flex items-center justify-center">
            <RefreshCw className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Rotation preview</div>
            <h3 className="font-heading text-lg font-extrabold text-foreground truncate">{location}</h3>
            {data?.slot_size && <div className="text-[11px] text-muted-foreground">Size · <b>{data.slot_size}</b> · {data.eligible_count} eligible creative{data.eligible_count === 1 ? '' : 's'}</div>}
          </div>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Rolls</label>
          <select value={rolls} onChange={(e) => setRolls(parseInt(e.target.value, 10))} className="rounded-lg border border-border bg-background px-2 py-1 text-xs" data-testid="rotation-preview-rolls">
            {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={run} disabled={loading} className="ml-auto rounded-full" data-testid="rotation-preview-rerun">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Rolling…' : 'Re-roll'}
          </Button>
        </div>

        {loading && !data && <div className="text-center py-6 text-sm text-muted-foreground">Simulating…</div>}
        {data && data.eligible_count === 0 && (
          <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-xl" data-testid="rotation-preview-empty">
            No creative is eligible for this slot right now. Add one to see it in the rotation.
          </div>
        )}
        {data && data.eligible_count > 0 && (
          <>
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">Winners</div>
              <ol className="space-y-1" data-testid="rotation-preview-rolls-list">
                {data.rolls.map((r) => (
                  <li key={r.roll} className="flex items-center gap-2 rounded-md border border-border bg-background/60 px-2 py-1.5">
                    <span className="text-[10px] font-mono text-muted-foreground w-6">#{r.roll}</span>
                    {r.media_asset_url && r.media_asset_url.match(/\.(jpg|jpeg|png|webp|gif)/i) && (
                      <img src={r.media_asset_url} alt="" className="h-6 w-6 rounded object-cover flex-shrink-0" />
                    )}
                    <span className="text-xs font-bold text-foreground truncate flex-1">{r.campaign_name}</span>
                    <span className="text-[9px] uppercase tracking-widest font-black text-muted-foreground">{r.priority}</span>
                    {r.rotation_mode === 'rotating' ? (
                      <span className="text-[9px] uppercase tracking-widest font-black bg-amber-500/20 text-amber-800 dark:text-amber-300 rounded-full px-1.5 py-0.5">Rot</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-widest font-black bg-primary/15 text-primary rounded-full px-1.5 py-0.5">Pin</span>
                    )}
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">Share summary</div>
              <ul className="space-y-1">
                {data.summary.map((s) => (
                  <li key={s.campaign_id} className="flex items-center gap-2 text-xs">
                    <span className="flex-1 truncate font-bold">{s.campaign_name}</span>
                    <span className="text-muted-foreground text-[10px]">weight {s.weight}</span>
                    <span className="tabular-nums font-mono text-foreground">{s.wins}/{data.rolls.length} · {s.share_pct}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatTile = ({ label, value, sub, tone, icon }) => (
  <div className={`rounded-xl border p-3 ${tone === 'warn' ? 'border-amber-300 bg-amber-50/60 dark:bg-amber-900/10' : tone === 'ok' ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/10' : 'border-border bg-muted/40'}`}>
    <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground inline-flex items-center gap-1">{icon} {label}</div>
    <div className="text-xl font-black text-foreground leading-tight">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
  </div>
);

export default AdSlotManager;
